import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import fileDownload from 'js-file-download';
import JSZip from 'jszip';
import Card from '../../components/common/Card';
import Button from '../../../incase-domain/components/button/Button';

const WORKSHOP_DIRECTORY = [
    'case-workshop/README.md',
    'case-workshop/AGENTS.md',
    'case-workshop/CLAUDE.md',
    'case-workshop/.gitignore',
    'case-workshop/.cursor/rules/presentation-workspace.mdc',
    'case-workshop/instructions/workspace.md',
    'case-workshop/instructions/html-presentation-prompt.md',
    'case-workshop/instructions/html-slide-prompt.md',
    'case-workshop/instructions/interactive/html-question-prompt.md',
    'case-workshop/instructions/interactive/html-multiselect-prompt.md',
    'case-workshop/instructions/interactive/html-sequence-prompt.md',
    'case-workshop/instructions/interactive/html-likert-scale-prompt.md',
    'case-workshop/instructions/interactive/html-question-report-prompt.md',
] as const;

const HTML_FILE_DOWNLOADS = WORKSHOP_DIRECTORY.flatMap((path) => {
    const filename = path.split('/').pop() ?? '';
    if (!filename.startsWith('html-')) {
        return [];
    }
    return [{href: path, filename}];
});

function downloadPrompt(href: string, filename: string): void {
    const link = document.createElement('a');
    link.href = `${import.meta.env.BASE_URL}${href}`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
}

async function downloadWorkshopDirectory(): Promise<void> {
    const zip = new JSZip();

    await Promise.all(
        WORKSHOP_DIRECTORY.map(async (path) => {
            const response = await fetch(`${import.meta.env.BASE_URL}${path}`);
            if (!response.ok) {
                throw new Error(path);
            }
            zip.file(path.replace(/^case-workshop\//, 'html-instructions/'), await response.arrayBuffer());
        })
    );

    const blob = await zip.generateAsync({type: 'blob'});
    fileDownload(blob, 'html-instructions.zip');
}

function HtmlPrompts(): JSX.Element {
    const {t} = useTranslation();
    const [downloading, setDownloading] = useState(false);

    const onDownloadDirectory = async (): Promise<void> => {
        setDownloading(true);
        try {
            await downloadWorkshopDirectory();
        } catch {
            toast.error(t('htmlPrompts.downloadFailed'));
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Card header={t('htmlPrompts.header')}>
            <div className="c-settingsItem">
                <div className="u-flex u-h-between u-v-top u-gap-4">
                    <div>
                        <p className="c-settingsItem__title">{t('htmlPrompts.directoryTitle')}</p>
                        <p className="c-settingsItem__description">{t('htmlPrompts.directoryDescription')}</p>
                    </div>
                    <Button
                        className="u-shrink-0"
                        size="sm"
                        variant="solid"
                        color="secondary"
                        leading="solar:download-minimalistic-linear"
                        loading={downloading}
                        onClick={onDownloadDirectory}
                    >
                        {t('htmlPrompts.downloadDirectory')}
                    </Button>
                </div>
            </div>
            <hr />
            <div className="c-settingsItem">
                <p className="c-settingsItem__title">{t('htmlPrompts.webTitle')}</p>
                <p className="c-settingsItem__description">{t('htmlPrompts.webDescription')}</p>
            </div>
            <div className="u-flex u-flex-col">
                {HTML_FILE_DOWNLOADS.map((prompt) => (
                    <div key={prompt.filename} className="c-settingsItem">
                        <div className="u-flex u-h-between u-v-center u-gap-4">
                            <p className="c-settingsItem__title">{prompt.filename}</p>
                            <Button
                                size="sm"
                                variant="outline"
                                color="secondary"
                                leading="solar:download-minimalistic-linear"
                                onClick={() => downloadPrompt(prompt.href, prompt.filename)}
                            >
                                {t('common.download')}
                            </Button>
                        </div>
                    </div>
                ))}
            </div>
        </Card>
    );
}

export default HtmlPrompts;
